import DataCoverageLine from "components/molecules/DataCoverageLine";
import { DataSource, Table } from "components/molecules/Table";
import SearchBar from "components/organisms/SearchBar/SearchBar";
import { useMemo } from "react";
import { TABLE_IDS } from "utils/constants";
import { getDisplayInfo } from "utils/formatting";
import { getDataCoverageMap, getDataCoverageSortValue } from "./Helper/PortfolioPage.helper";

interface FundsProps {
    funds: Fund[];
}

function Funds({ funds }: FundsProps) {
    const dataSource: DataSource[] = useMemo(
        () =>
            funds.map((fund) => ({
                ...fund,
                key: fund.fundId,
                buildingCount: fund.properties.reduce((count, property) => count + property.buildingModels.length, 0),
                propertyCount: fund.properties.length,
                tags: getTagsDisplayValue(fund),
                dataCoverages: <DataCoverageLine map={getDataCoverageMap(fund.properties)} />,
                dataCoverageSortValue: getDataCoverageSortValue(fund.properties),
            })),
        [funds]
    );

    if (dataSource.length === 0) {
        return null;
    }

    const columns = useMemo(
        () =>
            tableColumns.map((columnKey) => {
                const { formatFunction, humanReadable, unit, tooltip } =
                    typeof dataSource[0][columnKey] === "number"
                        ? getDisplayInfo(columnKey, dataSource.map((d) => d[columnKey]) as number[])
                        : getDisplayInfo(columnKey);

                const baseColumn = {
                    title: `${humanReadable} ${unit && `(${unit})`}`,
                    dataIndex: columnKey,
                    key: columnKey,
                    render: (value: any) => {
                        if (value === null || value === undefined) return "";
                        return formatFunction(value);
                    },
                    tooltip,
                };

                if (columnKey === "dataCoverages") {
                    return {
                        ...baseColumn,
                        customSort: (order: "asc" | "desc") => (a: DataSource, b: DataSource) => {
                            const aValue = a.dataCoverageSortValue as number;
                            const bValue = b.dataCoverageSortValue as number;

                            const result = aValue - bValue;
                            return order === "desc" ? -result : result;
                        },
                    };
                }

                return baseColumn;
            }),
        [dataSource]
    );

    return (
        <>
            <SearchBar tableId={TABLE_IDS.BUILDINGS} />
            <Table dataSource={dataSource} columns={columns} parentComponentWithSearchBarId={TABLE_IDS.FUNDS} />
        </>
    );
}

const getTagsDisplayValue = (fund: Fund) => {
    const tags: string[] = [];
    fund.properties.forEach((property) => {
        property.buildingModels.forEach((building) => {
            tags.push(...building.tags);
        });
    });
    const uniqueTags = new Set([...tags]);
    return Array.from(uniqueTags);
};

const tableColumns = [
    "fundName",
    "dataCoverages",
    "tags",
    "propertyCount",
    "buildingCount",
    "grossFloorArea",
    "carbonEmissionReductionPotential",
    "annualUtilityCostIntensityCurrent",
    "annualCarbonPolicyCostIntensityCurrent",
    "annualCarbonEmissionIntensityCurrent",
    "annualEnergyUseIntensityCurrent",
];

export default Funds;
